<template>
    <div v-if="isLoading" class="d-flex align-items-center" style="gap: 5px">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span>{{ $t('components.datatable.index.loadingText') }}</span>
    </div>
    <template v-else>
        <div
            class="alert alert-soft-warning text-primary"
            role="alert"
            style="background: #ef99381a; font-size: 12px; font-weight: 400; line-height: 21px"
            v-html="$t('views.inventory.modals.productDetail.offers.infoText', { url: getAmazonCountryURL, update: moment(offers?.last_updated).locale('tr').format('DD MMMM HH.mm') })"
        ></div>

        <div class="d-flex sm-flex-column" style="gap: 30px">
            <ImagePlaceholder :img_url="offers?.img_url" width="180" height="180" style="border-radius: 5px" />
            <div class="w-100">
                <div style="font-size: 14px; font-weight: 500; line-height: 21px; color: #00203f">
                    {{ offers?.title }}
                </div>

                <div class="row g-0" style="margin-top: 15px">
                    <div class="col-xs-12 col-lg-4">
                        <table class="table table-borderless table-bordered modal-table specs">
                            <tbody>
                                <tr>
                                    <td>ASIN</td>
                                    <td>
                                        <InventoryClipboard :asin="offers?.asin" />
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('views.inventory.modals.productDetail.offers.columns.sellerCount') }}</td>
                                    <td>{{ offers.offers?.length }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="col-xs-12 col-lg-4">
                        <table class="table table-borderless table-bordered modal-table specs">
                            <tbody>
                                <tr>
                                    <td>{{ $t('views.inventory.modals.productDetail.offers.columns.buyboxSeller') }}</td>
                                    <td>
                                        <a v-if="offers?.buybox_seller_id" :href="`https://www.${getAmazonCountryURL}/sp?seller=${offers?.buybox_seller_id}`" class="text-primary text-decoration-underline" target="_blank">{{
                                            offers?.buybox_seller_id
                                        }}</a>
                                        <template v-else>-</template>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('views.inventory.modals.productDetail.offers.columns.lowestSeller') }}</td>
                                    <td>
                                        <a v-if="offers?.lowest_seller_id" :href="`https://www.${getAmazonCountryURL}/sp?seller=${offers?.lowest_seller_id}`" class="text-primary text-decoration-underline" target="_blank">{{
                                            offers?.lowest_seller_id
                                        }}</a>
                                        <template v-else>-</template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="col-xs-12 col-lg-4">
                        <table class="table table-borderless table-bordered modal-table specs">
                            <tbody>
                                <tr>
                                    <td>{{ $t('views.inventory.modals.productDetail.offers.columns.fbaSeller') }}</td>
                                    <td v-if="offers?.is_fba">
                                        <i class="fa-solid fa-check text-success"></i>
                                    </td>
                                    <td v-else>
                                        <i class="fa-solid fa-x text-danger"></i>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('views.inventory.modals.productDetail.offers.columns.fbmSeller') }}</td>
                                    <td v-if="offers?.is_fbm">
                                        <i class="fa-solid fa-check text-success"></i>
                                    </td>
                                    <td v-else>
                                        <i class="fa-solid fa-x text-danger"></i>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <table class="table table-thead-bordered table-align-middle modal-table offers-table" style="margin-top: 20px">
            <thead class="thead-light">
                <tr>
                    <th>{{ $t('views.inventory.modals.productDetail.offers.columns.store') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.offers.columns.price') }}</th>
                    <th style="max-width: 50px"></th>
                    <th>FBA/FBM</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(offer, index) in offers.offers" :key="offer.id">
                    <td>
                        <a :href="`https://www.${getAmazonCountryURL}/sp?seller=${offer?.seller_id}`" class="text-primary text-decoration-underline" target="_blank">{{ offer?.seller_id }}</a
                        ><br />
                        <star-rating :round-start-rating="false" :star-size="14" :padding="3" pointer="none" :rating="offer.seller_rating" :showRating="false" :totalRating="5"></star-rating>
                    </td>
                    <td style="width: 108px">
                        {{ getCurrency }} <strong>{{ formatNumber(offer?.price) }}</strong>
                    </td>
                    <td style="max-width: 148px">
                        <div class="d-flex align-items-center" style="gap: 10px">
                            <span v-if="offer?.buybox_winner" class="badge bg-primary text-white">BuyBox</span>
                            <span v-if="index === 0" class="badge bg-secondary text-white">Lowest</span>
                        </div>
                    </td>
                    <td>{{ offer?.is_fba ? 'FBA' : 'FBM' }}</td>
                </tr>
            </tbody>
        </table>
    </template>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { countryInfo } from '@/utils/helper'
import InventoryClipboard from '../../InventoryClipboard.vue'
import ImagePlaceholder from '../../ImagePlaceholder.vue'

const countries = countryInfo()

export default {
    name: 'ProductDetailOffersModal',
    components: { InventoryClipboard, ImagePlaceholder },
    data() {
        return {
            countries,
            moment,
            isLoading: true,
        }
    },
    computed: {
        ...mapState('inventory', ['offers']),
        ...mapGetters('market', ['currentMarket']),
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
        getCurrency() {
            return countries[this.currentMarket.short].symbol
        },
    },
    created() {
        this.getOffers().finally(() => (this.isLoading = false))
    },
    methods: {
        ...mapActions('inventory', ['getOffers']),
        closeModal() {
            this.$emit('update:modelValue', false)
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY')
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
    },
    watch: {},
}
</script>

<style lang="scss">
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='ProductDetailOffersModal-'] {
    .modal-body {
        padding-top: 32px !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
    .modal-table {
        margin-bottom: 0 !important;
        border-radius: 5px;

        &.specs {
            td {
                width: 269px;
                height: 45px;

                &:nth-child(odd) {
                    border-right: 0 !important;
                }
            }
        }

        tbody {
            tr {
                td {
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 21px;
                    color: #677788;
                    height: 41px;
                }
                td:nth-child(odd) {
                    border-right: none;
                }
                td:nth-child(even) {
                    font-weight: 400;
                    color: #677788;
                    border-left: none;
                }
            }
        }

        .text-ellipsis {
            font-size: 11.9px;
            font-weight: 400;
            line-height: 17.9px;
            color: #677788;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            &.title {
                max-width: 250px;
            }

            &.category {
                max-width: 125px;
            }
        }
        .dp__input_wrap {
            width: auto;
            > input {
                padding-inline-end: 12px !important;
                font-size: 14px;
                border: 0.6px solid #e7eaf3;
                color: #677788;
                width: 125px !important;
            }
        }
    }

    .nav-segment {
        margin-right: 2rem;

        .nav-link {
            font-weight: 500;
            font-size: 14px;
        }
    }

    .multiselect {
        width: 140px !important;
        min-width: auto !important;

        &__placeholder {
            display: block !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
        }

        &__tags {
            padding: 12px 40px 0 8px !important;
        }

        &__select {
            line-height: normal !important;
        }
    }
}

.offers-table {
    td {
        height: 48px !important;
    }
}
</style>
